import React, { useEffect, useState } from "react";
import { ActivityCard, PlayButton, ProgressPill } from "../shared";

/**
 * QuestionFlip — "Ask with هَلْ" statement→question transformer.
 *
 * Each row shows a statement with a pulsing "+ هَلْ" button; tapping it
 * reveals the yes/no question with the fronted هَلْ (and the ؟) highlighted,
 * and plays it. Completes when every statement has been flipped.
 */
function QuestionWord({ questionAr }) {
  // Color the fronted question word and the closing ؟.
  const space = questionAr.indexOf(" ");
  if (space === -1) return questionAr;
  const first = questionAr.slice(0, space);
  let rest = questionAr.slice(space);
  let mark = null;
  if (rest.endsWith("؟")) {
    mark = <span style={{ color: "var(--tag-violet)" }}>؟</span>;
    rest = rest.slice(0, -1);
  }
  return (
    <>
      <span style={{ color: "var(--tag-violet)" }}>{first}</span>
      {rest}
      {mark}
    </>
  );
}

export default function QuestionFlip({ data, onComplete, audio }) {
  const [flippedIds, setFlippedIds] = useState(() => new Set());

  const items = data.items;
  const allFlipped = flippedIds.size >= items.length;

  useEffect(() => {
    if (allFlipped) onComplete();
  }, [allFlipped, onComplete]);

  const handleFlip = (item) => {
    if (flippedIds.has(item.id)) return;
    setFlippedIds((prev) => new Set(prev).add(item.id));
    audio.play(item.question_ar);
  };

  return (
    <ActivityCard>
      <div className="flex items-center justify-end mb-4">
        <ProgressPill>{flippedIds.size}/{items.length} questions asked</ProgressPill>
      </div>

      <div className="flex flex-col gap-3">
        {items.map((item) => {
          const flipped = flippedIds.has(item.id);
          return (
            <div
              key={item.id}
              className="rounded-xl px-4 py-4 grid grid-cols-[1fr_auto_1fr] items-center gap-3"
              style={{ background: "var(--ink)", border: "1px solid var(--panel-line)" }}
            >
              <div className="text-center">
                {/* pt gives Amiri's mark stacks headroom above the em box */}
                <p dir="rtl" lang="ar" className="font-arabic text-2xl sm:text-3xl leading-[1.8] m-0 pt-3" style={{ color: "var(--parchment)" }}>
                  {item.statement_ar}
                </p>
                <p className="text-xs mt-1 mb-0 inline-flex items-center gap-2" style={{ color: "var(--parchment-dim)" }}>
                  {item.statement_en}
                  {audio.audioAllowed && (
                    <PlayButton text={item.statement_ar} playingText={audio.playingText} onPlay={audio.play} />
                  )}
                </p>
              </div>

              <div className="flex justify-center">
                {flipped ? (
                  <span className="text-xl" aria-hidden="true" style={{ color: "var(--gold-dim)" }}>→</span>
                ) : (
                  <button
                    type="button"
                    onClick={() => handleFlip(item)}
                    className="rounded-lg px-3 pt-4 pb-1.5 font-arabic text-2xl leading-[1.4] transition-all hover:brightness-125 animate-pulse font-semibold"
                    style={{
                      color: "var(--gold-bright)",
                      background: "var(--gold-bg)",
                      border: "1px dashed var(--gold-dim)",
                    }}
                    title="Tap to put هَلْ in front and ask it"
                  >
                    {data.button_label || "+ هَلْ"}
                  </button>
                )}
              </div>

              <div className="text-center">
                {flipped ? (
                  <>
                    <p dir="rtl" lang="ar" className="font-arabic text-2xl sm:text-3xl leading-[1.8] m-0 pt-3" style={{ color: "var(--parchment)" }}>
                      <QuestionWord questionAr={item.question_ar} />
                    </p>
                    <p className="text-xs mt-1 mb-0 inline-flex items-center gap-2" style={{ color: "var(--parchment-dim)" }}>
                      {item.question_en}
                      {audio.audioAllowed && (
                        <PlayButton text={item.question_ar} playingText={audio.playingText} onPlay={audio.play} />
                      )}
                    </p>
                  </>
                ) : (
                  <p className="font-arabic text-3xl m-0" aria-hidden="true" style={{ color: "var(--parchment-dim)", opacity: 0.35 }}>
                    ؟
                  </p>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {allFlipped && (
        <div
          className="flex items-start gap-2.5 mt-4 px-4 py-3 rounded-xl"
          style={{ background: "var(--violet-bg)", border: "1px solid var(--panel-line)" }}
        >
          <span className="text-lg leading-none" aria-hidden="true">✨</span>
          <p className="text-sm leading-relaxed m-0" style={{ color: "var(--parchment)" }}>{data.takeaway}</p>
        </div>
      )}
    </ActivityCard>
  );
}
